'use client';

import { useEffect } from 'react';

/**
 * ProfileViewTracker — invisible client component that fires
 * `POST /api/public/:username/view` on mount to record a profile view.
 *
 * SPEC-119 Task D (Analytics tracking): the public Profile Hub page
 * (`/{username}`) is a Server Component (SSR) — it can't fire `fetch` in
 * a `useEffect` because it has no client-side lifecycle. This tiny
 * client component is rendered as a child of the Server Component —
 * it renders nothing visible + only runs an effect on mount (no UI,
 * no layout shift, no hydration concern).
 *
 * Fire-and-forget semantics: the `fetch` is NOT awaited — the `.catch(() => {})`
 * swallows any error (network failure, 4xx, 5xx, etc) so the visitor's UX
 * is NEVER affected. The backend endpoint itself returns 200 immediately
 * (it doesn't wait for the `$inc` write to complete either — see
 * `public.controller.ts:POST /:username/view` for details). Analytics is
 * best-effort throughout the stack.
 *
 * Why a separate component (not inline useEffect in the page)?
 *   - The page is a Server Component (SSR + ISR). Server Components can't
 *     use `useEffect` or browser APIs.
 *   - The same pattern as `<ReferralRefCapture />` (sibling component in
 *     `@/components/market/referral-ref-capture`) — invisible side-effect
 *     only client component.
 *
 * §6 Anti-Invención: pure analytics tracking — NO business logic. The
 * endpoint increments a counter; this component just fires the request.
 *
 * Anti-P-041: 0 `as any`. Anti-P-042: 0 console.* (errors are swallowed
 * silently — analytics must NEVER surface errors to the visitor).
 *
 * Spec ref: SPEC-119 §2 Task D, SPEC-NEGOCIO §1.4 (dashboard analytics).
 */

export interface ProfileViewTrackerProps {
  /** The profile owner's username — used to build the tracking endpoint URL. */
  username: string;
}

/**
 * Render nothing visible. This component is a side-effect-only client
 * component (fires a POST on mount). Returns `null` so it doesn't affect
 * layout / DOM.
 */
export function ProfileViewTracker({ username }: ProfileViewTrackerProps) {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    // SPEC-119 Task D: fire-and-forget POST to record the profile view.
    // The relative URL (`/api/public/...`) works because Next.js rewrites
    // `/api/*` to the backend in `next.config.ts` (or env-configured proxy
    // in production). If the rewrite is missing, the fetch fails silently
    // (analytics is non-critical — the visitor's UX is unaffected).
    //
    // The `.catch(() => {})` is critical: without it, an unhandled promise
    // rejection could surface in the browser console (noisy + scary for
    // visitors) + in some setups trigger the React error boundary (which
    // would render an error page on top of the profile — catastrophic UX
    // for a non-critical analytics call).
    fetch(`/api/public/${encodeURIComponent(username)}/view`, {
      method: 'POST',
    }).catch(() => {
      // Intentionally swallow — analytics must NEVER break the UX.
    });
  }, [username]);

  return null;
}
